import { placeHolderImage } from '@/constants/placeHolderImage';
import { useEnsureCartId } from '@/hooks/use-ensure-cart-id';
import { formatString } from '@/lib/format-string';
import { cn } from '@/lib/utils';
import { generateUrl } from '@/util/imageUtils';
import { useMutation } from '@apollo/client';
import Decimal from 'decimal.js';
import Image from 'next/image';
import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import FormError from '../FormError';
import { useMiniCartStore } from '../MiniCart/use-new-minicart-store';
import Price from '../Price';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AddToCartLoadingIcon from './add-to-cart-loading-icon';
import { ADD_PRODUCT_TO_CART } from './use-add-to-cart';

const QuantityBundle = props => {
  const { product, isOutOfStock, currencyCode, variantInStockMap, variantImageMap, variantPriceValueMap } = props;
  const formValues = useWatch();
  const [selectedBundleIndex, setSelectedBundleIndex] = useState(-1);
  const qtyBundleRules = useMemo(
    () =>
      Array.isArray(product.qty_bundle_rules) ? product.qty_bundle_rules.slice(0).sort((a, b) => a.qty - b.qty) : [],
    [product.qty_bundle_rules],
  );
  const bundleProductRefs = useRef<Record<string, any[]>>(
    qtyBundleRules.reduce((acc, cur, i) => {
      acc[i] = [];
      return acc;
    }, {}),
  );
  const [addToCartLoading, setAddToCartLoading] = useState(false);

  const setCartStatus = useMiniCartStore(state => state?.setCartStatus);
  const [addProductToCart, { error: errorAddingProductToCart }] = useMutation(ADD_PRODUCT_TO_CART);
  const { handleEnsureCartId } = useEnsureCartId();
  const [showCreateCartError, setShowCreateCartError] = useState(false);
  const errors = (
    [
      errorAddingProductToCart,
      showCreateCartError ? new Error('Failed to create a new cart, Please refresh the page and try again') : null,
    ] as Error[]
  ).filter(Boolean);

  if (!product) return null;

  const configurableOptions = product.configurable_options || [];
  const isCpProduct = product.__typename === 'ConfigurableProduct';

  // 只在单属性cp产品显示quantity bundle的功能，因为做了缺货选项不显示，在多属性产品子sku交叉缺货时会出现选项无法切换的问题
  if (!isCpProduct || configurableOptions.length !== 1) return null;

  const isAllOptionSelected =
    Array.isArray(configurableOptions) && configurableOptions.length > 0
      ? configurableOptions.every(x => {
          const attributeCode = x?.attribute_code;

          if (!attributeCode) return false;

          return !!formValues[attributeCode]?.uid;
        })
      : false;
  const isDealsUnlock = isAllOptionSelected && !isOutOfStock;

  const handleAddToCart = async () => {
    if (bundleProductRefs.current[selectedBundleIndex].some(x => x?.isOutOfStock)) return;

    if (addToCartLoading) return;

    setAddToCartLoading(true);
    setShowCreateCartError(false);
    const nextCartId = await handleEnsureCartId();

    if (!nextCartId) {
      setAddToCartLoading(false);
      setShowCreateCartError(true);
      return;
    }

    const attributeCodeOrder =
      configurableOptions.reduce((acc, cur, i) => {
        if (!cur?.attribute_code) return acc;

        acc[cur.attribute_code] = i;
        return acc;
      }, {}) || {};

    await Promise.all(
      bundleProductRefs.current[selectedBundleIndex].map(({ selectedOptions, addToCartFile }) => {
        return (async () => {
          if (configurableOptions.length > selectedOptions.length) return;

          const selectedUids = selectedOptions
            .slice(0)
            .sort((a, b) => (attributeCodeOrder[a.attributeCode] || 0) - (attributeCodeOrder[b.attributeCode] || 0))
            .map(x => x.uid);

          track({
            product,
            quantity: 1,
          });

          try {
            await addProductToCart({
              variables: {
                cartId: nextCartId,
                product: {
                  sku: product.sku,
                  quantity: 1,
                  category_id: product?.categories?.[0]?.uid ? parseInt(atob(product.categories[0].uid), 10) : 0,
                  category_name: product?.categories?.[0]?.name || product?.categories?.[0]?.breadcrumbs?.[0]?.category_name || '',
                  product_image:
                    process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL +
                    generateUrl(addToCartFile || '', 'image-product')(90, 132),
                  selected_options: selectedUids,
                },
                entered_options: [
                  {
                    uid: product.uid,
                    value: product.name,
                  },
                ],
              },
            });
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error(error);
            }
          }
        })();
      }),
    );

    setCartStatus('addtocart');
    setAddToCartLoading(false);
  };

  const maxDiscountNumber = qtyBundleRules.reduce((acc, cur) => {
    const price = cur?.price;

    if (price === null || price === undefined) return acc;

    if (acc === null) return price;

    return Math.max(cur.price, acc);
  }, null);
  const ruleType = qtyBundleRules[0]?.type;

  const renderDiscountContent = (type, price) => {
    if (price === undefined || price === null) return null;

    if (type === 'PERCENT') return `${price}%`;

    if (type === 'FIXED')
      return (
        <Price
          value={price}
          currencyCode={currencyCode}
        />
      );

    return null;
  };

  return (
    <div className={cn('mt-5 rounded-[10px] bg-[#F7F7F5] px-[14px] py-5', isDealsUnlock ? 'px-2.5 lg_px-[35px]' : '')}>
      <div className='text-center font-poppins text-[14px] font-semibold leading-[1.2] text-[#000] lg_text-[16px]'>
        Buy More, Save Up to&nbsp;
        <span className='font-sans'>{renderDiscountContent(ruleType, maxDiscountNumber)}</span>
      </div>
      <div className={cn('mt-2.5 text-center lg_mt-5', isDealsUnlock ? 'lg_mt-2.5' : '')}>
        {isDealsUnlock ? null : (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='none'
            viewBox='0 0 24 24'
            className='inline-block'
          >
            <path
              fill='#272636'
              d='M17.669 8.714h-.41V6.753c0-1.39-.53-2.7-1.493-3.691a5.21 5.21 0 0 0-3.774-1.578c-2.954 0-5.269 2.314-5.269 5.269v1.96h-.39a2.665 2.665 0 0 0-2.665 2.665v8.381a2.665 2.665 0 0 0 2.665 2.665h11.336a2.665 2.665 0 0 0 2.665-2.665v-8.38a2.665 2.665 0 0 0-2.665-2.665M7.692 6.753c0-2.411 1.889-4.3 4.3-4.3 1.181 0 2.275.456 3.08 1.284.786.809 1.219 1.88 1.219 3.016v1.96H7.692zm11.673 13.006c0 .937-.76 1.696-1.696 1.696H6.333c-.937 0-1.696-.76-1.696-1.696v-8.38c0-.937.76-1.697 1.696-1.697h11.336c.936 0 1.696.76 1.696 1.696z'
            ></path>
            <path
              fill='#272636'
              d='M11.953 13.602a.484.484 0 0 0-.484.484v2.568a.485.485 0 0 0 .969 0v-2.568a.484.484 0 0 0-.485-.484'
            ></path>
          </svg>
        )}
        <span className={cn('ml-2.5 font-sans text-[14px] leading-[1.4] text-[#000]', isDealsUnlock ? 'ml-0' : '')}>
          {isDealsUnlock
            ? 'Set up multiple feeding spots with less cost.'
            : 'Select an in stock option above to unlock the deals'}
        </span>
      </div>

      {isDealsUnlock ? (
        <>
          <ul className='mt-2.5 flex flex-col gap-y-2.5 lg_gap-y-5'>
            {qtyBundleRules.map((rule, i) => {
              if (!rule || !rule.qty || !rule.type || rule.price === undefined || rule.price === null) return null;

              const isSelected = selectedBundleIndex === i;

              return (
                <RuleCard
                  key={`${rule.qty}-${i}`}
                  isSelected={isSelected}
                  setSelectedBundleIndex={setSelectedBundleIndex}
                  renderDiscountContent={renderDiscountContent}
                  rule={rule}
                  errors={errors}
                  addToCartLoading={addToCartLoading}
                  bindRef={(node, index) => {
                    const list = bundleProductRefs.current[i] || [];
                    list[index] = node;
                  }}
                  handleAddToCart={handleAddToCart}
                  product={product}
                  variantInStockMap={variantInStockMap}
                  variantImageMap={variantImageMap}
                  variantPriceValueMap={variantPriceValueMap}
                  currencyCode={currencyCode}
                  onSelect={() => {
                    setSelectedBundleIndex(i);
                  }}
                />
              );
            })}
          </ul>
          <div className='mt-2.5 text-center text-[#000]'>*Bundle already discounted. No extra codes allowed. </div>
        </>
      ) : null}
    </div>
  );
};

const RuleCard = props => {
  const {
    isSelected,
    renderDiscountContent,
    errors,
    addToCartLoading,
    bindRef,
    handleAddToCart,
    product,
    variantInStockMap,
    variantImageMap,
    rule,
    currencyCode,
    onSelect,
    variantPriceValueMap,
  } = props;

  const [skuPriceList, setSkuPriceList] = useState<number[]>(Array.from({ length: rule.qty }, () => 0));
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => setIsMounted(true), []);

  const totalPrice = skuPriceList.reduce((acc, cur) => Decimal(acc).plus(cur).toNumber(), 0);
  const totalFinalPrice = skuPriceList.reduce<number>((acc, cur) => {
    if (rule.type === 'PERCENT') {
      const discountPercent = Math.min(Math.max(rule.price, 0), 100);
      const productFinalPrice = Decimal.max(Decimal(cur).mul(Decimal(100).minus(discountPercent)).div(100), 0).toFixed(
        2,
      );
      return Decimal(acc).plus(Decimal(productFinalPrice)).toNumber();
    }

    if (rule.type === 'FIXED') {
      const discountAmount = Math.max(rule.price, 0);
      const productFinalPrice = Decimal.max(Decimal(cur).minus(discountAmount), 0).toFixed(2);
      return Decimal(acc).plus(Decimal(productFinalPrice)).toNumber();
    }

    return acc;
  }, 0);
  const savedValue = Decimal(totalPrice).minus(totalFinalPrice).toNumber();

  return (
    <li
      className={cn(
        'relative cursor-pointer rounded-md border-1 border-solid border-[#ddd] bg-white',
        isSelected && 'border-primary',
      )}
      onClick={onSelect}
    >
      {savedValue > 0 && !isSelected ? (
        <div className='absolute -top-3 left-[46px] rounded-[30px] bg-secondary px-[5px] py-1 font-poppins text-[10px] font-semibold leading-[1.2] text-white lg_hidden'>
          <span>Save</span>
          &nbsp;
          <span>
            <Price
              value={savedValue}
              currencyCode={currencyCode}
            />
          </span>
        </div>
      ) : null}

      <div
        className={cn(
          'flex items-center justify-between p-2.5 lg_py-5 lg_pl-5 lg_pr-3',
          isSelected ? 'flex-col gap-y-[5px] bg-primary lg_flex-row lg_justify-between lg_pl-[26px] ' : '',
        )}
      >
        <div
          className={cn('flex items-center gap-x-2.5', isSelected && 'flex-col gap-y-[5px] lg_flex-row lg_gap-x-[5px]')}
        >
          <div
            className={cn(
              ' h-4 w-4 flex-none items-center justify-center border-1 border-solid border-title',
              isSelected ? 'hidden border-primary bg-white lg_flex' : 'flex',
            )}
          >
            {isSelected ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='12'
                height='9'
                fill='none'
                viewBox='0 0 12 9'
              >
                <path
                  stroke='#2F6132'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeMiterlimit='10'
                  strokeWidth='1.5'
                  d='m.75 4.25 3.333 3.5 6.667-7'
                ></path>
              </svg>
            ) : null}
          </div>
          <div
            className={cn(
              'font-sans text-[16px] leading-[1.4] text-[#000] lg_leading-[1.2]',
              isSelected && 'font-normal text-white',
            )}
          >
            Buy {rule.qty} Get {renderDiscountContent(rule.type, rule.price)} Off
          </div>

          {savedValue > 0 ? (
            <div
              className={cn(
                'hidden rounded-[30px] bg-secondary px-[5px] py-1 font-poppins text-[10px] font-semibold leading-[1.2] text-white lg_block',
                isSelected && 'block bg-transparent p-0 text-[26px] leading-[1.4] text-white lg_hidden lg_text-[18px]',
              )}
            >
              <span>Save</span>
              &nbsp;
              <span>
                <Price
                  value={savedValue}
                  currencyCode={currencyCode}
                />
              </span>
            </div>
          ) : null}
        </div>

        <div className={cn('flex items-center gap-x-2.5 text-[#000]', isSelected && ' text-white')}>
          {savedValue > 0 && isSelected ? (
            <div
              className={cn(
                'hidden rounded-[30px] font-poppins text-[18px] font-semibold leading-[1.4] text-white lg_block ',
              )}
            >
              <span>Save</span>
              &nbsp;
              <span>
                <Price
                  value={savedValue}
                  currencyCode={currencyCode}
                />
              </span>
              !
            </div>
          ) : null}
          <span className={cn('font-sans text-[14px] leading-[1.4] line-through', !isMounted && 'invisible')}>
            <Price
              value={totalPrice}
              currencyCode={currencyCode}
            />
          </span>
          <span
            className={cn(
              'font-poppins text-[14px] font-semibold',
              isSelected && 'text-[18px]',
              !isMounted && 'invisible',
            )}
          >
            <Price
              value={totalFinalPrice}
              currencyCode={currencyCode}
            />
          </span>
        </div>
      </div>
      <div className={cn(!isSelected ? 'hidden' : '', 'py-5')}>
        <ul className='flex flex-col gap-y-5'>
          {Array.from({ length: rule.qty }, (_, i) => (
            <li
              key={i}
              className='px-[19px]'
            >
              <QuantityBundleProduct
                product={product}
                variantInStockMap={variantInStockMap}
                variantImageMap={variantImageMap}
                ref={node => {
                  bindRef(node, i);
                }}
                setSkuPriceList={setSkuPriceList}
                variantPriceValueMap={variantPriceValueMap}
                productIndex={i}
                currencyCode={currencyCode}
                rule={rule}
              />
            </li>
          ))}
        </ul>
        {isSelected ? (
          <>
            {Array.isArray(errors) && errors.length > 0 ? (
              <div>
                <FormError
                  errors={errors}
                  scrollOnError={false}
                  allowErrorMessages
                />
              </div>
            ) : null}
            <div className='mt-5 px-2.5'>
              <button
                type='button'
                onClick={handleAddToCart}
                disabled={addToCartLoading}
                className='flex min-h-[40px] w-full items-center justify-center rounded-[10px] bg-primary  text-center text-[14px] font-semibold leading-[1.2] text-white outline outline-0 outline-transparent lg_min-h-[54px] lg_text-[16px] lg_leading-[1.4] lg_enabled_hover_opacity-90'
              >
                {addToCartLoading ? <AddToCartLoadingIcon /> : <>ADD {rule.qty} TO CART</>}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </li>
  );
};

const QuantityBundleProduct = forwardRef<any, any>(
  (
    {
      product,
      variantInStockMap,
      variantImageMap,
      variantPriceValueMap,
      setSkuPriceList,
      productIndex,
      currencyCode,
      rule,
    },
    ref,
  ) => {
    const configurableOptions = product?.configurable_options || [];

    const formValues = useWatch({
      name: (product?.configurable_options || []).map(x => x.attribute_code),
    });

    const prevPropsRef = useRef({ formValues });

    const initSelectedOptions = formValues => {
      if (!Array.isArray(configurableOptions) || configurableOptions.length === 0) return [];

      return configurableOptions.reduce((acc, cur, i) => {
        const mainProductSelectedUid = formValues?.[i]?.uid;
        const validValues =
          cur?.values?.filter(
            value => variantInStockMap[value?.value_index] === 1 || variantInStockMap[value?.value_index] === 0,
          ) || [];

        const value = mainProductSelectedUid
          ? validValues?.find(x => x?.uid === mainProductSelectedUid)
          : validValues?.[0];

        if (!value) return acc;

        acc.push({
          attributeCode: cur?.attribute_code,
          valueIndex: value.value_index,
          uid: value.uid,
          label: value.default_label,
        });

        return acc;
      }, []);
    };

    const [selectedOptions, setSelectedOptions] = useState(() => initSelectedOptions(formValues));

    if (prevPropsRef.current.formValues !== formValues) {
      prevPropsRef.current.formValues = formValues;
      setSelectedOptions(initSelectedOptions(formValues));
    }

    const selectedOptionTag = selectedOptions
      .map(x => x.valueIndex)
      .sort()
      .join(',');

    const isOutOfStock = variantInStockMap[selectedOptionTag] !== 1;
    const imageUrl = variantImageMap[selectedOptionTag]?.url;
    const price = variantPriceValueMap[selectedOptionTag];

    const renderFinalPrice = (() => {
      if (!rule) return null;

      if (rule.type === 'PERCENT') {
        const discountPercent = Math.min(Math.max(rule.price, 0), 100);
        return parseFloat(Decimal.max(Decimal(price).mul(Decimal(100).minus(discountPercent)).div(100), 0).toFixed(2));
      }

      if (rule.type === 'FIXED') {
        const discountAmount = Math.max(rule.price, 0);
        return parseFloat(Decimal.max(Decimal(price).minus(discountAmount), 0).toFixed(2));
      }
    })();

    useEffect(() => {
      setSkuPriceList(prev => prev.map((x, prevIndex) => (prevIndex === productIndex ? price : x)));
    }, [setSkuPriceList, price, productIndex]);

    useImperativeHandle(ref, () => ({
      isOutOfStock,
      selectedOptions,
      addToCartFile: variantImageMap[selectedOptionTag]?.file,
    }));

    if (!product) return null;

    return (
      <div className='flex items-start gap-x-5'>
        <div className='h-[70px] w-[70px] flex-none rounded-[10px] border-1 border-solid border-[#ddd] lg_h-[110px] lg_w-[110px]'>
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt=''
              width={120}
              height={120}
              className='h-full w-full object-cover'
              quality={95}
              placeholder={placeHolderImage}
            />
          ) : null}
        </div>
        <div className='w-full lg_max-w-[274px]'>
          <div className='font-sans text-[16px] leading-[1.2] text-[#000]'>{formatString(product.name || '')}</div>
          <div className='mt-2.5 flex items-center gap-x-2.5 text-[#000]'>
            <span className='font-sans text-[14px] leading-[1.2] line-through'>
              <Price
                currencyCode={currencyCode}
                value={price}
              />
            </span>
            <span className='font-sans text-[16px] leading-[1.2]'>
              <Price
                currencyCode={currencyCode}
                value={renderFinalPrice}
              />
            </span>
          </div>
          {Array.isArray(configurableOptions) && configurableOptions.length > 0 ? (
            <div className='mt-2.5 flex flex-col gap-y-2.5'>
              {configurableOptions.map(configurableOption => {
                if (
                  !configurableOption?.attribute_code ||
                  !Array.isArray(configurableOption.values) ||
                  configurableOption.values.length === 0
                )
                  return null;

                const selectedUid = selectedOptions.find(
                  selectedOption => selectedOption.attributeCode === configurableOption.attribute_code,
                )?.uid;

                const renderValues = configurableOption.values.filter(
                  value =>
                    variantInStockMap[
                      selectedOptions
                        .reduce((acc, cur) => {
                          acc.push(
                            cur.attributeCode === configurableOption.attribute_code
                              ? value.value_index
                              : cur.valueIndex,
                          );
                          return acc;
                        }, [])
                        .sort()
                        .join(',')
                    ] === 1,
                );

                const handleChange = nextUid => {
                  const value = renderValues.find(x => x.uid === nextUid);

                  if (!value) return;

                  setSelectedOptions(prev =>
                    prev
                      .filter(selectedOption => selectedOption.attributeCode !== configurableOption.attribute_code)
                      .concat({
                        attributeCode: configurableOption.attribute_code,
                        valueIndex: value.value_index,
                        uid: value.uid,
                        label: value.default_label,
                      }),
                  );
                };

                return (
                  <Fragment key={configurableOption.attribute_code}>
                    <div className='relative lg_hidden'>
                      <select
                        value={selectedUid}
                        className=' w-full appearance-none rounded-md border-1 border-solid border-[#ddd] px-2.5 py-1.5 font-sans text-[16px] text-[#000] outline outline-0 outline-transparent lg_min-h-[34px]  lg_rounded  lg_pr-[17px]'
                        onChange={e => {
                          handleChange(e.target.value);
                        }}
                      >
                        {renderValues.map(x => {
                          if (!x?.uid || !x?.default_label) return null;

                          return (
                            <option
                              key={x.uid}
                              value={x.uid}
                            >
                              {x.default_label}
                            </option>
                          );
                        })}
                      </select>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='11'
                        height='6'
                        fill='none'
                        viewBox='0 0 11 6'
                        className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 '
                      >
                        <path
                          stroke='#666'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='m.5.5 4.643 5 4.643-5'
                        ></path>
                      </svg>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className='relative hidden lg_block'>
                          <div className='flex w-full items-center rounded-md border-1 border-solid border-[#ddd] px-2.5 py-1.5 font-sans text-[16px] text-[#000] outline outline-0 outline-transparent lg_min-h-[34px]  lg_rounded  lg_pr-[17px]'>
                            {selectedOptions.find(x => x.attributeCode === configurableOption.attribute_code)?.label}
                          </div>

                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='12'
                            height='7'
                            fill='none'
                            viewBox='0 0 12 7'
                            className='pointer-events-none absolute right-2.5 top-1/2  -translate-y-1/2  '
                          >
                            <path
                              stroke='#222'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth='1.5'
                              d='m.75.75 5 5 5-5'
                            ></path>
                          </svg>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className='![animation-duration:64ms]'>
                        <DropdownMenuGroup>
                          <DropdownMenuRadioGroup
                            value={selectedUid}
                            onValueChange={handleChange}
                          >
                            {renderValues.map(x => {
                              if (!x?.uid || !x?.default_label) return null;

                              return (
                                <DropdownMenuRadioItem
                                  key={x.uid}
                                  value={x.uid}
                                  className='text-[16px]'
                                >
                                  {x.default_label}
                                </DropdownMenuRadioItem>
                              );
                            })}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Fragment>
                );
              })}
            </div>
          ) : null}

          {isOutOfStock ? <div className='mt-2.5 text-[14px] text-red'>OUT OF STOCK</div> : null}
        </div>
      </div>
    );
  },
);

const track = ({ product, quantity }: { product: any; quantity: number }) => {
  try {
    if (product) {
      const currency =
        product?.price_range?.minimum_price?.final_price?.currency ||
        product?.price_range?.maximum_price?.final_price?.currency;
      const price =
        product?.price_range?.minimum_price?.final_price?.value ||
        product?.price_range?.maximum_price?.final_price?.value;

      window.dataLayer &&
        window.dataLayer.push({
          ecommerce: null,
        });
      const dl = (window as any).dataLayer || [];
      const viewItemListEvent = [...dl].reverse().find((event: any) => event.event === 'view_item_list');
      
      let fallbackName = (product as any)?.categories?.[0]?.name || (product as any)?.categories?.[0]?.breadcrumbs?.[0]?.category_name || '';
      if (!fallbackName) {
          if (window.location.pathname.includes('search')) {
              fallbackName = 'Search Results';
          } else {
              fallbackName = document.title.split('-')[0].trim();
          }
      }
      
      const categoryName = viewItemListEvent?.ecommerce?.item_list_name || fallbackName;

      const ga4Payload = {
          event: 'add_to_cart',
          ecommerce: {
            currency: currency,
            value: price * quantity,
            quantity: quantity,
            items: [
              {
                item_name: product.name,
                item_id: product.sku,
                item_category: categoryName,
                item_list_name: categoryName,
                price: price,
                quantity: quantity,
              },
            ],
          },
      };
      console.log('👉 [GA4 Event] add_to_cart triggered', ga4Payload);
      window.dataLayer && window.dataLayer.push(ga4Payload);

      // Pinterest
      window.pintrk &&
        window.pintrk('track', 'addtocart', {
          value: price * quantity,
          order_quantity: quantity,
          currency: currency,
          line_items: [
            {
              product_name: product.name,
              product_id: product.uid,
              product_category: null,
              product_price: price,
              product_brand: 'kingsyard',
            },
          ],
        });

      // fbq
      window.fbq &&
        window.fbq('track', 'AddToCart', {
          content_ids: [product.sku],
          content_name: product.name,
          content_type: 'product',
          contents: [
            {
              product_name: product.name,
              product_id: product.uid,
              product_category: null,
              product_price: price,
              product_brand: 'kingsyard',
            },
          ],
          currency: currency,
          value: price * quantity,
        });
    }
  } catch {
    error => {
      console.error(error);
    };
  }
};

export default QuantityBundle;
